/**
 * moduleTrains.ts — curated module bundles ("trains") that cover the full
 *                   diffuser→plume→bed→O₂→algae→compliance pipeline.
 *
 * Each train is verified internally to be conflict-free (no hard conflicts;
 * soft conflicts are explicitly documented in the rationale).
 */

import { MODULES } from '@/lib/moduleRegistry';

export interface TrainStage {
  /** Process stage label shown on the summary diagram. */
  stage: 'Diffuser' | 'Plume' | 'Bed' | 'Hypolimnion O₂' | 'Algae' | 'Compliance' | 'Optimisation';
  /** Module IDs from the registry that are active in this stage. */
  moduleIds: string[];
  /** Plain-English description of what the stage delivers. */
  delivers: string;
}

export interface ModuleTrain {
  id: string;
  label: string;
  shortDesc: string;
  longDesc: string;
  /** Typical site characteristics this train targets. */
  bestFor: string;
  /** Full module list in execution order. */
  moduleIds: string[];
  /** Process pipeline broken into stages. */
  stages: TrainStage[];
  /** Known acceptable soft-conflict pairs within this train (explained for user). */
  acceptedSoftConflicts: { a: string; b: string; reason: string }[];
}

export const MODULE_TRAINS: ModuleTrain[] = [
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'shallow_uk_eutrophic',
    label: 'A · Shallow Eutrophic UK Reservoir',
    shortDesc: 'Barcombe-like: 3-6 m mean depth, high chl-a, organic cohesive bed',
    longDesc:
      'Targets the typical UK lowland storage reservoir at risk of summer cyanobacterial blooms and ' +
      'taste-and-odour problems. Uses shallow-plume physics (Asaeda-Imberger), proper cohesive-sediment bed ' +
      'mechanics (Mehta-Partheniades + Aller bioturbation + Boudreau), and the full cyano feedback / ' +
      'co-limitation / geosmin chain. Wind-tilt corrects for exposed sites.',
    bestFor: 'Mean depth 3-6 m, surface area 1-50 ha, summer chl-a peaks >20 µg/L, organic-rich bed, weak stratification.',
    moduleIds: [
      'asaeda_c', 'deep_zone', 'wind_tilt',
      'boudreau_diagenesis', 'aller_bioturbation', 'mehta_partheniades',
      'nom_sod',
      'kumagai_feedback', 'np_colimitation', 'csr_groups',
      'geosmin_predictor',
      'cost_per_o2',
      'tem_epilimnetic',  // alternate summer operating regime
    ],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: '£/kg-O₂ ranking selects the cost-optimal diffuser type.' },
      { stage: 'Plume', moduleIds: ['asaeda_c', 'deep_zone', 'wind_tilt', 'tem_epilimnetic'], delivers: 'Plume merging regime, deep-zone placement bias, wind-tilt deflection. TEM is the summer alternate regime — reduced Q_air to confine plume to the surface mixed layer.' },
      { stage: 'Bed', moduleIds: ['boudreau_diagenesis', 'aller_bioturbation', 'mehta_partheniades', 'nom_sod'], delivers: 'Porewater fluxes, biodiffusion enhancement, erosion/deposition regime, NOM-driven SOD.' },
      { stage: 'Hypolimnion O₂', moduleIds: ['nom_sod'], delivers: 'Hypolimnetic O₂ depletion rate from NOM-enhanced SOD.' },
      { stage: 'Algae', moduleIds: ['kumagai_feedback', 'np_colimitation', 'csr_groups'], delivers: 'Cyano-feedback amplification, dual N+P limitation, functional-group succession. TEM directly breaks the Kumagai feedback loop when active.' },
      { stage: 'Compliance', moduleIds: ['geosmin_predictor'], delivers: 'Geosmin/2-MIB breach forecast vs DWI 10 ng/L.' },
    ],
    acceptedSoftConflicts: [
      { a: 'deep_zone', b: 'resusp_penalty', reason: 'resusp_penalty NOT included — Mehta-Partheniades supersedes it.' },
      { a: 'tem_epilimnetic', b: 'deep_zone', reason: 'Deep-zone diffusers retained as hardware; TEM throttles airflow during warm-weather windows so the plume is surface-confined. Both modules co-exist; train notes which regime is active.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'deep_stratified_storage',
    label: 'B · Deep Stratified Storage Reservoir',
    shortDesc: 'Bewl/Ardingly-like: 15-40 m, strong summer stratification, deep hypolimnion',
    longDesc:
      'Deep stratified reservoirs need detraining bubble-plume physics (Wüest 1992 double-plume) instead of the ' +
      'Asaeda-Imberger surface-reaching model. Bubble dissolution and CH₄ oxidation become depth-sensitive ' +
      '(McGinnis 2006). Internal seiche resonance must be checked. Hypolimnetic Fe(II)/Mn(II) accumulation ' +
      '(Davison 1993) is the primary WTW risk. MacIntyre convection fills the night-time mixing gap.',
    bestFor: 'Depth > 15 m, N > 0.005 s⁻¹, stratification > 6 weeks/year, downstream WTW must meet Mn 50 µg/L PCV.',
    moduleIds: [
      'wuest_double_plume', 'mcginnis_bubble', 'higbie_kl', 'coalescence', 'deep_zone',
      'internal_seiche',
      'macintyre_convection',
      'boudreau_diagenesis', 'redox_p_release',
      'davison_fe_mn',
      'reckhow_p_retention',
      'cost_per_o2',
    ],
    stages: [
      { stage: 'Diffuser', moduleIds: ['mcginnis_bubble', 'higbie_kl', 'coalescence', 'cost_per_o2'], delivers: 'Depth-resolved OTE with bubble coalescence and Higbie kL. Cost ranking.' },
      { stage: 'Plume', moduleIds: ['wuest_double_plume', 'deep_zone', 'internal_seiche'], delivers: 'Detrainment height & lateral intrusion at the pycnocline; seiche resonance check.' },
      { stage: 'Bed', moduleIds: ['boudreau_diagenesis', 'redox_p_release'], delivers: 'Porewater profile + ORP-driven internal P loading.' },
      { stage: 'Hypolimnion O₂', moduleIds: ['davison_fe_mn'], delivers: 'Fe/Mn half-life kinetics — main WTW operational risk metric.' },
      { stage: 'Algae', moduleIds: ['reckhow_p_retention'], delivers: 'Cross-check on Vollenweider P-loading prediction.' },
      { stage: 'Compliance', moduleIds: ['davison_fe_mn'], delivers: 'Mn-PCV breach forecast vs DWI 50 µg/L.' },
      { stage: 'Optimisation', moduleIds: [], delivers: '(none — manual placement recommended for deep schemes)' },
    ],
    acceptedSoftConflicts: [
      { a: 'mcginnis_bubble', b: 'higbie_kl', reason: 'McGinnis supersedes Higbie internally; both are on so the diagnostic shows both. No correction needed.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'cyano_toxin_focus',
    label: 'C · Cyanotoxin & Taste/Odour Focus',
    shortDesc: 'High-risk T&O reservoir requiring PAC at WTW; bloom mitigation priority',
    longDesc:
      'Targets reservoirs where cyanobacterial taste-and-odour metabolites (geosmin, 2-MIB) drive customer ' +
      'complaints and PAC dosing OPEX. Couples bloom physics (Kumagai feedback + CSR functional groups + ' +
      'N+P co-limitation) with empirical Wang 2010 metabolite prediction. Sediment capping appears as a ' +
      'mitigation option alongside aeration.',
    bestFor: 'Sites with documented geosmin/2-MIB > 10 ng/L, PAC cost > £20 k/yr, cyano fraction > 30 %.',
    moduleIds: [
      'asaeda_c', 'wind_tilt',
      'kumagai_feedback', 'np_colimitation', 'csr_groups',
      'sediment_capping',
      'geosmin_predictor',
      'reckhow_p_retention',
      'cost_per_o2',
    ],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: 'Cost-of-O₂ benchmarking.' },
      { stage: 'Plume', moduleIds: ['asaeda_c', 'wind_tilt'], delivers: 'Plume regime and wind correction.' },
      { stage: 'Bed', moduleIds: ['sediment_capping'], delivers: 'La-bentonite cap as P-release mitigation option.' },
      { stage: 'Hypolimnion O₂', moduleIds: [], delivers: '(handled by Algal Dynamics directly)' },
      { stage: 'Algae', moduleIds: ['kumagai_feedback', 'np_colimitation', 'csr_groups', 'reckhow_p_retention'], delivers: 'Full bloom physics with limitation diagnostics.' },
      { stage: 'Compliance', moduleIds: ['geosmin_predictor'], delivers: 'PAC dosing requirement forecast.' },
    ],
    acceptedSoftConflicts: [],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'anoxic_mn_internal_p',
    label: 'D · Anoxic Hypolimnion · Mn & Internal P Loading',
    shortDesc: 'Stratified reservoir with low DO and high internal nutrient release',
    longDesc:
      'Targets reservoirs where summer hypolimnetic anoxia drives Fe/Mn release (Davison 1993) and ' +
      'sediment P internal loading (Ma & Wu 2008). Combines Boudreau diagenesis with NOM-SOD enhancement to ' +
      'predict anoxia onset timing. Optional Aller bioturbation tracks the recovery after aeration starts.',
    bestFor: 'Stratification > 4 weeks; hypolimnion DO < 2 mg/L by July; sediment OM > 8 %.',
    moduleIds: [
      'deep_zone', 'wind_tilt',
      'boudreau_diagenesis', 'aller_bioturbation', 'nom_sod',
      'redox_p_release', 'davison_fe_mn',
      'sediment_capping',
      'reckhow_p_retention',
      'cost_per_o2',
    ],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: 'Cost ranking.' },
      { stage: 'Plume', moduleIds: ['deep_zone', 'wind_tilt'], delivers: 'Plume placement biased to deep zone.' },
      { stage: 'Bed', moduleIds: ['boudreau_diagenesis', 'aller_bioturbation', 'nom_sod', 'sediment_capping'], delivers: 'Full porewater + biodiffusion + NOM + cap-layer accounting.' },
      { stage: 'Hypolimnion O₂', moduleIds: ['redox_p_release', 'davison_fe_mn'], delivers: 'P, Fe and Mn fluxes triggered by ORP collapse.' },
      { stage: 'Algae', moduleIds: ['reckhow_p_retention'], delivers: 'Vollenweider/Reckhow cross-check on resulting P loading.' },
      { stage: 'Compliance', moduleIds: ['davison_fe_mn'], delivers: 'Mn breakthrough vs DWI 50 µg/L PCV.' },
    ],
    acceptedSoftConflicts: [
      { a: 'redox_p_release', b: 'davison_fe_mn', reason: 'Same underlying ORP physics, complementary outputs (P vs Fe/Mn) — designed to co-run.' },
      { a: 'aller_bioturbation', b: 'boudreau_diagenesis', reason: 'Aller multiplies Boudreau — designed to co-run, not double-counted.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'multiobj_optimisation',
    label: 'E · Multi-Objective Optimisation Run',
    shortDesc: 'Algorithmic placement optimisation across CAPEX / OPEX / coverage',
    longDesc:
      'Runs DDS single-objective search and NSGA-II Pareto evolution simultaneously. Pareto front exposes the ' +
      'cost-vs-coverage trade-off. Compatible with the deep-zone preference (acts as a constraint, not an objective).',
    bestFor: 'Sites where placement is non-trivial and the engineer wants algorithmic guidance.',
    moduleIds: ['dds_optimiser', 'nsga2', 'deep_zone', 'cost_per_o2'],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: 'CAPEX/OPEX objective term.' },
      { stage: 'Optimisation', moduleIds: ['dds_optimiser', 'nsga2', 'deep_zone'], delivers: 'DDS single-objective + NSGA-II Pareto front with deep-zone bias.' },
      { stage: 'Plume', moduleIds: [], delivers: '(optimisation drives placement — plume diagnostics inherited from chosen point)' },
      { stage: 'Bed', moduleIds: [], delivers: '(combine with Trains A or B for bed physics)' },
      { stage: 'Hypolimnion O₂', moduleIds: [], delivers: '' },
      { stage: 'Algae', moduleIds: [], delivers: '' },
      { stage: 'Compliance', moduleIds: [], delivers: '' },
    ],
    acceptedSoftConflicts: [
      { a: 'dds_optimiser', b: 'deep_zone', reason: 'Deep-zone preference acts as a soft constraint on the DDS search bounds.' },
      { a: 'nsga2', b: 'deep_zone', reason: 'Same — deep zone biases the initial population.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'high_fidelity_3d',
    label: 'F · High-Fidelity 3-D Validation',
    shortDesc: 'Use GLM 1-D + ELCOM 3-D outputs to validate placement',
    longDesc:
      'For schemes where a 3-D thermal/water-quality model is available (or affordable), couples the in-tool ' +
      'placement solver to GLM (1-D vertical) and ELCOM-CAEDYM (3-D) outputs. Acts as a peer-review layer on ' +
      'the simpler analytical predictions.',
    bestFor: 'Late-stage design with budget for offline ELCOM run; AMP-7 / AMP-8 strategic schemes.',
    moduleIds: ['glm_coupling', 'elcom_coupling', 'asaeda_c', 'wuest_double_plume', 'internal_seiche', 'cost_per_o2'],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: 'Cost ranking.' },
      { stage: 'Plume', moduleIds: ['asaeda_c', 'wuest_double_plume', 'internal_seiche'], delivers: 'Both shallow and deep plume models — pick whichever matches depth regime.' },
      { stage: 'Bed', moduleIds: [], delivers: '(ELCOM-CAEDYM handles sediment fluxes if configured)' },
      { stage: 'Hypolimnion O₂', moduleIds: ['glm_coupling'], delivers: 'Vertical O₂ profile from GLM.' },
      { stage: 'Algae', moduleIds: ['elcom_coupling'], delivers: '3-D phytoplankton field from CAEDYM if loaded.' },
      { stage: 'Compliance', moduleIds: [], delivers: '(driven by chosen specialist modules from Trains A-D)' },
    ],
    acceptedSoftConflicts: [
      { a: 'asaeda_c', b: 'wuest_double_plume', reason: 'Both plume models loaded for comparison purposes; whichever matches the depth regime is the operational result.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'warm_water_summer',
    label: 'G · Warm-water Summer Regime (TEM-led)',
    shortDesc: 'July-August Barcombe / warm-water shallow reservoir; cyano risk window',
    longDesc:
      'Specialised train for the warm-weather window in shallow polymictic reservoirs (Barcombe summer ' +
      'July-September, similar UK lowland sites, Australian comparators Chaffey Dam / Cotter Dam). ' +
      'Targeted Epilimnetic Mixing (TEM) leads — reduced airflow on the existing diffuser hardware to ' +
      'mix only the surface warm layer where Microcystis accumulate, preserving the cold raw-water ' +
      'supply zone for the WTW. The Kumagai cyano-stratification feedback module flags when the ' +
      'feedback loop is active (TEM directly breaks it); MacIntyre nocturnal convection covers the ' +
      'natural-mixing complement; Wuest detrainment height is collapsed to the SML base; geosmin ' +
      'predictor closes the loop against DWI compliance. Energy demand 5-15 % of full destratification.',
    bestFor: 'Mean depth 3-8 m, July-August surface T > 18 °C, surface-hypo ΔT > 4 °C, cyano fraction > 0.25, downstream WTW abstracts from hypolimnion.',
    moduleIds: [
      'tem_epilimnetic',
      'kumagai_feedback', 'np_colimitation', 'csr_groups',
      'macintyre_convection',
      'wuest_double_plume', 'mcginnis_bubble',
      'nom_sod',
      'geosmin_predictor',
      'cost_per_o2',
    ],
    stages: [
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: '£/kg-O₂ ranking — same hardware as the year-round train, throttled.' },
      { stage: 'Plume', moduleIds: ['tem_epilimnetic', 'wuest_double_plume', 'mcginnis_bubble'], delivers: 'TEM-led: airflow set to terminate plume at SML base. Wuest detrainment + McGinnis OTE re-integrated over the shorter rise path.' },
      { stage: 'Bed', moduleIds: ['nom_sod'], delivers: 'NOM-SOD continues to apply; bed-erosion modules omitted because TEM does not disturb the hypolimnion.' },
      { stage: 'Hypolimnion O₂', moduleIds: ['nom_sod'], delivers: 'Hypolimnetic O₂ trajectory monitored — if depletion > critical, switch to full-mix train (A or B).' },
      { stage: 'Algae', moduleIds: ['kumagai_feedback', 'np_colimitation', 'csr_groups', 'macintyre_convection'], delivers: 'Kumagai feedback flagged (TEM breaks it); MacIntyre adds nocturnal natural-mixing; co-limitation + CSR succession tracked.' },
      { stage: 'Compliance', moduleIds: ['geosmin_predictor'], delivers: 'Geosmin / 2-MIB forecast vs DWI 10 ng/L; TEM is the primary upstream control.' },
    ],
    acceptedSoftConflicts: [
      { a: 'tem_epilimnetic', b: 'mcginnis_bubble', reason: 'McGinnis OTE re-cast over SML depth; the formulation is unchanged but the integration limit changes — handled in train logic.' },
      { a: 'tem_epilimnetic', b: 'deep_zone', reason: 'Train G does NOT include deep_zone — TEM regime targets the epilimnion, not the deepest 30 % of the basin.' },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────
  {
    id: 'year_round_adaptive_barcombe',
    label: 'H · Year-Round Adaptive Barcombe (recommended)',
    shortDesc: 'Seasonal scheduler: winter-OFF / spring-LOW / summer-PWCM (shallow) or TEM (deep) / autumn-controlled',
    longDesc:
      'The recommended year-round operating strategy for Barcombe and similar UK lowland shallow polymictic ' +
      'reservoirs. The Seasonal Adaptive Aeration Scheduler is the orchestrator - it switches the aeration ' +
      'regime continuously across the annual cycle (winter OFF, spring conditioning LOW, summer mixing regime, ' +
      'autumn controlled-mix). The summer-mixing regime auto-selects based on mean depth: ' +
      'PWCM (Visser-Verspagen 1996 / CIRIA C753) for shallow polymictic basins (< 5 m mean depth) where ' +
      'whole-column overturn is the correct strategy; TEM (Patterson 1984) for deeper stratified reservoirs ' +
      '(>= 5 m) where preserving a cold hypolimnion matters. Synthesises Cooke 2005 management framework, ' +
      'Antenucci-Imberger Lake-Number criterion, Beutel-Horne 1999 envelope, Gachter-Wehrli 1998 long-term ' +
      'Sempach evidence, Visser-Verspagen Lake Nieuwe Meer (NL) 25-year evidence, and the Australian TEM lineage. ' +
      'Energy demand 30-50 % of continuous year-round operation at equal or better water-quality outcome.',
    bestFor: 'Barcombe and analogous UK lowland reservoirs - mean depth 3-8 m, polymictic, summer cyano-prone, downstream WTW abstraction. Default summer regime: PWCM for < 5 m mean, TEM for >= 5 m.',
    moduleIds: [
      'seasonal_scheduler',          // year-round orchestrator
      'pwcm_visser',                 // summer regime - shallow polymictic (Barcombe default)
      'tem_epilimnetic',             // summer regime - deep stratified (alternate)
      'kumagai_feedback',
      'macintyre_convection',
      'wuest_double_plume',
      'mcginnis_bubble',
      'asaeda_c',
      // ─── Bed-physics stack (full set used by strategyCYearRound.ts) ───
      'mehta_partheniades',          // cohesive-bed shear vs tau_ce
      'nom_sod',                     // NOM-driven SOD (Mueller-Burris-Boyle)
      'aller_bioturbation',          // porewater diffusion enhancement
      'boudreau_diagenesis',         // reaction-diffusion porewater fluxes
      'sediment_capping',            // optional cap-layer mitigation
      'resusp_penalty',              // diffuser-over-silt flag
      'redox_p_release',             // anoxia-driven internal P pulse
      'davison_fe_mn',               // autumn turnover Mn/Fe control
      // ─── Other ───
      'np_colimitation',
      'csr_groups',
      'geosmin_predictor',
      'cost_per_o2',
    ],
    stages: [
      { stage: 'Optimisation', moduleIds: ['seasonal_scheduler'], delivers: 'Year-round regime switching - OFF / LOW / TEM / FULL based on Lake Number and stratification state.' },
      { stage: 'Diffuser', moduleIds: ['cost_per_o2'], delivers: 'Single hardware set sized for FULL regime; operated at lower setpoints in TEM and LOW.' },
      { stage: 'Plume', moduleIds: ['tem_epilimnetic', 'wuest_double_plume', 'mcginnis_bubble', 'asaeda_c'], delivers: 'TEM-led in summer; McGinnis OTE re-cast over SML depth when TEM active; Wuest detrainment; Asaeda merging regime.' },
      { stage: 'Bed', moduleIds: ['mehta_partheniades', 'nom_sod', 'aller_bioturbation', 'boudreau_diagenesis', 'sediment_capping', 'resusp_penalty', 'redox_p_release', 'davison_fe_mn'], delivers: 'Full bed-physics stack: cohesive-bed shear vs tau_ce (Mehta-Partheniades); NOM-driven SOD (Mueller-Burris-Boyle); porewater diffusion (Aller bioturbation + Boudreau diagenesis); engineered cap option; per-position resuspension flag; redox-driven internal P pulse; autumn-turnover Fe/Mn release.' },
      { stage: 'Hypolimnion O2', moduleIds: ['nom_sod', 'davison_fe_mn', 'redox_p_release'], delivers: 'O2 status drives whether scheduler escalates to FULL regime; redox switches gate internal-flux modules.' },
      { stage: 'Algae', moduleIds: ['kumagai_feedback', 'np_colimitation', 'csr_groups', 'macintyre_convection'], delivers: 'Cyano feedback flagged (broken by TEM); MacIntyre adds nocturnal natural mixing; CSR and N+P co-limitation track succession.' },
      { stage: 'Compliance', moduleIds: ['geosmin_predictor', 'davison_fe_mn'], delivers: 'Geosmin / 2-MIB forecast against DWI 10 ng/L; Mn / Fe release vs DWI PCVs; scheduler ramps regime up if breach predicted.' },
    ],
    acceptedSoftConflicts: [
      { a: 'tem_epilimnetic', b: 'mcginnis_bubble', reason: 'McGinnis OTE auto-recasts to the SML depth when scheduler tag = TEM.' },
      { a: 'seasonal_scheduler', b: 'all', reason: 'Scheduler operates above the other modules - never conflicts with the physics modules it orchestrates.' },
      // ─── Summer-window mixing strategies (mutually exclusive selection per regime) ───
      { a: 'pwcm_visser', b: 'tem_epilimnetic', reason: 'Mutually exclusive summer-window mixing strategies - scheduler auto-selects PWCM for mean depth < 5 m (Barcombe default) and TEM for deeper basins. Only one is engaged in any given timestep.' },
      // ─── Bed-stack co-running modules (designed to co-run, NOT double-counted) ───
      { a: 'aller_bioturbation', b: 'boudreau_diagenesis', reason: 'Aller bioturbation is a 1.8× multiplier on the Boudreau porewater diffusion baseline - designed to co-run as a coupled pair, not double-counted.' },
      { a: 'redox_p_release', b: 'davison_fe_mn', reason: 'Same underlying ORP physics, complementary outputs (P vs Fe/Mn). Designed to co-run with shared DO_hypo trigger gating.' },
      { a: 'nom_sod', b: 'mehta_partheniades', reason: 'SOD demand (Mueller-Burris-Boyle) and bed shear (Mehta-Partheniades) operate on independent axes (demand vs erosion). Designed to co-run.' },
      { a: 'sediment_capping', b: 'redox_p_release', reason: 'Engineered cap reduces J_int when redox switch fires - capping suppresses but does not eliminate the redox release flux. Designed to co-run when cap is applied.' },
      { a: 'resusp_penalty', b: 'mehta_partheniades', reason: 'Per-position substrate flag (resusp) and basin-level shear (Mehta) operate at different spatial scales. Designed to co-run.' },
    ],
  },
];

/** Helper: get a train by id. */
export function getTrain(id: string): ModuleTrain | undefined {
  return MODULE_TRAINS.find(t => t.id === id);
}

/** Resolve module objects from IDs (skips missing). */
export function resolveTrainModules(train: ModuleTrain) {
  return train.moduleIds.map(id => MODULES.find(m => m.id === id)).filter(Boolean);
}
